import React, { useState, useEffect, useRef, useCallback } from 'react';


const ProductAutocomplete = ({ value, onChange, onSelect, warehouseId, companyId, placeholder = "Rechercher SKU, Nom..." }) => {
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [loading, setLoading] = useState(false);
    const wrapperRef = useRef(null);
    const justSelected = useRef(false);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchSuggestions = useCallback(async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 2) {
            setSuggestions([]);
            return;
        }

        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                companyId,
                search: searchTerm,
            });
            if (warehouseId) {
                queryParams.append('warehouseId', warehouseId);
            }

            const response = await fetch(`/api/products?${queryParams.toString()}`);
            if (response.ok) {
                const data = await response.json();
                // Handle both array (legacy) and paginated object (new) formats
                const products = Array.isArray(data) ? data : (data.products || []);
                setSuggestions(products);
                setShowSuggestions(products.length > 0);
            }
        } catch (error) {
            console.error('Error fetching suggestions:', error);
        } finally {
            setLoading(false);
        }
    }, [companyId, warehouseId]);


    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (justSelected.current) {
                justSelected.current = false;
                return;
            }
            if (value && value.length >= 2) {
                fetchSuggestions(value);
            }
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [value, fetchSuggestions]);


    const handleSelect = (product) => {
        justSelected.current = true;
        onSelect(product);
        setShowSuggestions(false);
        setSuggestions([]);
    };

    return (
        <div className="relative w-full" ref={wrapperRef}>
            <div className="relative group">
                <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors">tag</span>
                <input
                    className="w-full pl-12 pr-4 py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-primary text-slate-900 dark:text-white font-semibold placeholder:text-slate-500"
                    placeholder={placeholder}
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onFocus={() => {
                        if (suggestions.length > 0) setShowSuggestions(true);
                    }}
                />
                {loading && (
                    <div className="absolute right-4 top-1/2 -translate-y-1/2">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent"></div>
                    </div>
                )}
            </div>

            {showSuggestions && (
                <div className="absolute z-[100] w-full mt-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                    <ul className="max-h-60 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
                        {suggestions.map((p) => (
                            <li
                                key={p.id}
                                className="p-4 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors"
                                onClick={() => handleSelect(p)}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="font-bold text-slate-900 dark:text-white text-sm">{p.name}</p>
                                        <div className="flex gap-2 items-center mt-1">
                                            <span className="text-[10px] bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded font-mono text-slate-600 dark:text-slate-400">SKU: {p.sku}</span>
                                            {p.externalReference && (
                                                <span className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded font-mono">CRM: {p.externalReference}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-xs font-black text-primary">{p.quantity} en stock</p>
                                        <p className="text-[10px] text-slate-400">{p.warehouse?.name || 'Inconnu'}</p>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default ProductAutocomplete;
